import { Link } from "react-router-dom";
import Navbar from "@/components/Navbar";
import {
  ArrowLeft,
  ShieldCheck,
  Lock,
  Cookie,
  Mail,
  UserCheck,
  Database,
  CreditCard,
  Send,
  Trash2,
  Eye,
  Ban,
} from "lucide-react";

// Privacy Policy for Cinevoya. Drafted against: Nigeria Data Protection
// Act 2023 (NDPA), NDPR 2019 (interpretive), Cybercrimes Act 2015, and
// aligned with GDPR principles for diaspora viewers. A Nigerian-licensed
// attorney should review before relying on it in a dispute.
const EFFECTIVE_DATE = "September 2026";
const CONTACT_EMAIL = "privacy@cinevoya.com";

export default function PrivacyPolicy() {
  return (
    <div className="min-h-screen bg-ink grain">
      <Navbar />
      <div
        className="pt-28 px-6 sm:px-12 lg:px-20 max-w-3xl mx-auto pb-24"
        data-testid="privacy-policy-page"
      >
        <Link
          to="/"
          className="inline-flex items-center gap-1.5 text-sm text-terracotta hover:text-ember mb-6"
          data-testid="privacy-back-link"
        >
          <ArrowLeft className="w-3.5 h-3.5" /> Back to home
        </Link>

        <div className="flex items-center gap-3 mb-2">
          <Lock className="w-6 h-6 text-gold" />
          <p className="text-xs uppercase tracking-widest text-terracotta font-semibold">
            Legal · Privacy
          </p>
        </div>
        <h1 className="font-display text-4xl sm:text-5xl font-black mb-3">
          Privacy Policy
        </h1>
        <p className="text-foreground/55 text-sm">
          Effective {EFFECTIVE_DATE} · Governed by the laws of the Federal
          Republic of Nigeria (NDPA 2023)
        </p>

        <div
          className="mt-4 p-4 rounded-lg border border-gold/30 bg-gold/5 flex items-start gap-2.5"
          data-testid="privacy-disclaimer"
        >
          <ShieldCheck className="w-4 h-4 text-gold mt-0.5 shrink-0" />
          <p className="text-xs text-foreground/75 leading-relaxed">
            Cinevoya respects your privacy. This policy explains what personal
            data we collect when you use our platform, why we collect it, who
            we share it with, and the rights you have over that data. By using
            Cinevoya you agree to the practices described here.
          </p>
        </div>

        <div className="prose-invert mt-10 space-y-10 text-foreground/85 text-[15px] leading-relaxed">
          <section data-testid="privacy-section-collected">
            <div className="flex items-center gap-2 mb-3">
              <Database className="w-5 h-5 text-terracotta" />
              <h2 className="font-display text-2xl font-bold text-foreground">
                1. Data we collect
              </h2>
            </div>
            <p>
              We collect only what we need to run Cinevoya, protect our
              filmmakers, and give you a smooth viewing experience:
            </p>
            <ul className="list-disc pl-5 space-y-1.5 mt-2 text-foreground/80">
              <li>
                <strong>Account details</strong> — your name, email address,
                phone number, chosen role (Viewer or Filmmaker), and the
                password hash you set at sign-up.
              </li>
              <li>
                <strong>Payment information</strong> — when you rent a film or
                top up your wallet, our payment processors collect your card /
                bank details directly. Cinevoya never stores full card numbers
                or bank passwords. We do keep a transaction reference, the
                amount, the currency (NGN or USD), and the timestamp.
              </li>
              <li>
                <strong>Filmmaker content</strong> — for creators, we store
                your uploaded videos, posters, synopsis, cast/crew credits,
                bank-account routing information (used solely for payouts),
                and any festival-request forms you submit.
              </li>
              <li>
                <strong>Usage data</strong> — pages visited, films rented, the
                approximate country your IP resolves to (used to price in NGN
                for Nigeria vs USD for diaspora), device type, and any error
                logs that help us fix bugs.
              </li>
              <li>
                <strong>Communications</strong> — the content of emails you
                send our support desk and the RSVPs / film submissions you
                make through our event and submission forms.
              </li>
            </ul>
          </section>

          <section data-testid="privacy-section-usage">
            <div className="flex items-center gap-2 mb-3">
              <UserCheck className="w-5 h-5 text-terracotta" />
              <h2 className="font-display text-2xl font-bold text-foreground">
                2. How we use your data
              </h2>
            </div>
            <p>We use your personal data for the following purposes:</p>
            <ul className="list-disc pl-5 space-y-1.5 mt-2 text-foreground/80">
              <li>
                <strong>Account management</strong> — to create and secure
                your account, authenticate logins, reset forgotten passwords,
                verify your email, and enforce role-based access (Viewer,
                Filmmaker, Admin).
              </li>
              <li>
                <strong>Payment processing</strong> — to charge for rentals,
                credit your wallet, pay filmmaker earnings, refund cancelled
                transactions, and comply with Nigerian tax and anti-fraud
                obligations.
              </li>
              <li>
                <strong>Communications</strong> — to send transactional emails
                (rental receipts, password resets, event tickets, submission
                confirmations) and, only if you opt in, occasional updates
                about new films, launch events, and platform features.
              </li>
              <li>
                <strong>Curation &amp; safety</strong> — to review filmmaker
                submissions, detect fraudulent uploads, protect the platform
                against abuse, and honour DMCA / copyright takedown requests.
              </li>
              <li>
                <strong>Platform improvement</strong> — to measure which films
                and features are popular so we can invest editorial and
                engineering effort where it matters.
              </li>
            </ul>
          </section>

          <section data-testid="privacy-section-third-parties">
            <div className="flex items-center gap-2 mb-3">
              <CreditCard className="w-5 h-5 text-terracotta" />
              <h2 className="font-display text-2xl font-bold text-foreground">
                3. Third-party services we use
              </h2>
            </div>
            <p>
              Cinevoya relies on a small, carefully-chosen set of processors.
              Each one only receives the minimum data it needs to do its job,
              and each has its own privacy policy that we've reviewed:
            </p>
            <ul className="list-disc pl-5 space-y-3 mt-3 text-foreground/80">
              <li>
                <div className="flex items-center gap-2 mb-1">
                  <CreditCard className="w-4 h-4 text-gold" />
                  <strong>Paystack</strong> (Naira payments)
                </div>
                Processes rentals and wallet top-ups paid in Nigerian Naira.
                Receives your name, email, phone number, and card / bank
                details entered on their secure checkout — never on Cinevoya
                servers. See{" "}
                <a
                  href="https://paystack.com/privacy"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-terracotta hover:text-ember underline"
                >
                  paystack.com/privacy
                </a>
                .
              </li>
              <li>
                <div className="flex items-center gap-2 mb-1">
                  <CreditCard className="w-4 h-4 text-gold" />
                  <strong>Flutterwave</strong> (USD / diaspora payments)
                </div>
                Processes rentals and wallet top-ups paid in US Dollars for
                viewers outside Nigeria. Receives the same minimum-necessary
                identity + payment details. See{" "}
                <a
                  href="https://flutterwave.com/privacy-policy"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-terracotta hover:text-ember underline"
                >
                  flutterwave.com/privacy-policy
                </a>
                .
              </li>
              <li>
                <div className="flex items-center gap-2 mb-1">
                  <Send className="w-4 h-4 text-gold" />
                  <strong>Resend</strong> (transactional email)
                </div>
                Sends transactional and event emails on our behalf. Receives
                your email address, name, and the content of the message
                being sent (e.g. a rental receipt or launch-night RSVP). See{" "}
                <a
                  href="https://resend.com/legal/privacy-policy"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-terracotta hover:text-ember underline"
                >
                  resend.com/legal/privacy-policy
                </a>
                .
              </li>
            </ul>
            <p className="mt-4 text-foreground/70 text-sm">
              We do <strong>not</strong> sell your data to advertisers or data
              brokers. We do not share your data with any third party other
              than the processors listed above, except where required by
              Nigerian law or a valid court order.
            </p>
          </section>

          <section data-testid="privacy-section-rights">
            <div className="flex items-center gap-2 mb-3">
              <ShieldCheck className="w-5 h-5 text-terracotta" />
              <h2 className="font-display text-2xl font-bold text-foreground">
                4. Your rights
              </h2>
            </div>
            <p>
              Under the Nigeria Data Protection Act 2023 (and GDPR for
              diaspora viewers), you have the following rights over your
              personal data. To exercise any of them, email us at{" "}
              <a
                href={`mailto:${CONTACT_EMAIL}`}
                className="text-terracotta hover:text-ember underline"
                data-testid="privacy-rights-email"
              >
                {CONTACT_EMAIL}
              </a>{" "}
              from the address on your account — we respond within 30 days.
            </p>
            <ul className="list-disc pl-5 space-y-2 mt-3 text-foreground/80">
              <li>
                <span className="inline-flex items-center gap-1.5 font-semibold text-foreground">
                  <Eye className="w-3.5 h-3.5 text-gold" /> Right of access
                </span>{" "}
                — request a copy of the personal data we hold about you.
              </li>
              <li>
                <span className="inline-flex items-center gap-1.5 font-semibold text-foreground">
                  <UserCheck className="w-3.5 h-3.5 text-gold" /> Right to
                  rectify
                </span>{" "}
                — correct anything that's inaccurate or out of date. Most
                fields you can edit yourself from your profile page.
              </li>
              <li>
                <span className="inline-flex items-center gap-1.5 font-semibold text-foreground">
                  <Trash2 className="w-3.5 h-3.5 text-gold" /> Right to
                  deletion
                </span>{" "}
                — ask us to erase your account and personal data. We honour
                deletion within 30 days, except where we're legally required
                to keep records (e.g. financial transactions for tax audits,
                retained for up to 7 years).
              </li>
              <li>
                <span className="inline-flex items-center gap-1.5 font-semibold text-foreground">
                  <Ban className="w-3.5 h-3.5 text-gold" /> Right to opt out
                </span>{" "}
                — unsubscribe from marketing emails any time via the link in
                every email footer, or reply STOP to any SMS. Transactional
                messages (receipts, security alerts, event tickets) cannot be
                opted out of while your account is active.
              </li>
              <li>
                <span className="inline-flex items-center gap-1.5 font-semibold text-foreground">
                  <Send className="w-3.5 h-3.5 text-gold" /> Right to
                  portability
                </span>{" "}
                — receive your data in a machine-readable format so you can
                move it to another service.
              </li>
              <li>
                <span className="inline-flex items-center gap-1.5 font-semibold text-foreground">
                  <ShieldCheck className="w-3.5 h-3.5 text-gold" /> Right to
                  complain
                </span>{" "}
                — you may lodge a complaint with the Nigeria Data Protection
                Commission (NDPC) if you believe we've mishandled your data.
              </li>
            </ul>
          </section>

          <section data-testid="privacy-section-cookies">
            <div className="flex items-center gap-2 mb-3">
              <Cookie className="w-5 h-5 text-terracotta" />
              <h2 className="font-display text-2xl font-bold text-foreground">
                5. Cookies &amp; local storage
              </h2>
            </div>
            <p>
              Cinevoya uses a small number of first-party cookies and
              browser-local-storage entries — nothing is sold or shared with
              advertising networks:
            </p>
            <ul className="list-disc pl-5 space-y-1.5 mt-2 text-foreground/80">
              <li>
                <strong>Session cookie</strong> — an HttpOnly, Secure cookie
                that keeps you logged in for 7 days. Strictly necessary; the
                site can't authenticate you without it.
              </li>
              <li>
                <strong>Preference storage</strong> — a lightweight
                localStorage entry that remembers whether you've dismissed
                install prompts, the launch-night banner, or the offline
                viewing snapshot. No personal data leaves your device.
              </li>
              <li>
                <strong>Payment processor cookies</strong> — Paystack and
                Flutterwave may set cookies on their own checkout domains for
                fraud prevention. These are governed by their own privacy
                policies.
              </li>
            </ul>
            <p className="mt-3 text-foreground/70 text-sm">
              You can clear cookies and local storage at any time from your
              browser settings. Doing so will log you out and reset your
              in-browser preferences.
            </p>
          </section>

          <section data-testid="privacy-section-retention">
            <div className="flex items-center gap-2 mb-3">
              <Database className="w-5 h-5 text-terracotta" />
              <h2 className="font-display text-2xl font-bold text-foreground">
                6. How long we keep your data
              </h2>
            </div>
            <p>
              We keep account data for as long as your account is active. If
              you delete your account, we erase your profile within 30 days,
              retaining only the minimum records needed to meet legal
              obligations (typically up to 7 years for financial transaction
              records under Nigerian tax law).
            </p>
          </section>

          <section data-testid="privacy-section-security">
            <div className="flex items-center gap-2 mb-3">
              <Lock className="w-5 h-5 text-terracotta" />
              <h2 className="font-display text-2xl font-bold text-foreground">
                7. Security
              </h2>
            </div>
            <p>
              Passwords are hashed with bcrypt before storage — we never see
              your plaintext password. All traffic between your device and
              Cinevoya is encrypted in transit via TLS 1.2+. Our database
              access is restricted, credentials are rotated, and payment data
              is tokenised by Paystack / Flutterwave rather than stored on
              our infrastructure. No system is 100% secure — if we detect a
              breach affecting your data we will notify you within 72 hours
              in line with the NDPA.
            </p>
          </section>

          <section data-testid="privacy-section-children">
            <div className="flex items-center gap-2 mb-3">
              <ShieldCheck className="w-5 h-5 text-terracotta" />
              <h2 className="font-display text-2xl font-bold text-foreground">
                8. Children
              </h2>
            </div>
            <p>
              Cinevoya is not directed at children under 13, and we do not
              knowingly collect data from anyone under 13. If you believe a
              child has created an account, email us at{" "}
              <a
                href={`mailto:${CONTACT_EMAIL}`}
                className="text-terracotta hover:text-ember underline"
              >
                {CONTACT_EMAIL}
              </a>{" "}
              and we will delete the account.
            </p>
          </section>

          <section data-testid="privacy-section-changes">
            <div className="flex items-center gap-2 mb-3">
              <Send className="w-5 h-5 text-terracotta" />
              <h2 className="font-display text-2xl font-bold text-foreground">
                9. Changes to this policy
              </h2>
            </div>
            <p>
              We may update this policy occasionally as the platform evolves
              or the law changes. When we do, we'll bump the effective date
              at the top of this page and, for material changes, notify you
              by email at least 14 days before the change takes effect.
            </p>
          </section>

          <section data-testid="privacy-section-contact">
            <div className="flex items-center gap-2 mb-3">
              <Mail className="w-5 h-5 text-terracotta" />
              <h2 className="font-display text-2xl font-bold text-foreground">
                10. Contact us
              </h2>
            </div>
            <p>
              For any privacy enquiry, data-rights request, or complaint,
              contact our data-protection team:
            </p>
            <div className="mt-3 p-5 rounded-xl border border-terracotta/30 bg-terracotta/5">
              <p className="text-sm text-foreground/70 mb-1 flex items-center gap-2">
                <Mail className="w-4 h-4 text-terracotta" /> Privacy enquiries
              </p>
              <a
                href={`mailto:${CONTACT_EMAIL}`}
                className="font-display text-xl font-bold text-foreground hover:text-terracotta transition-colors"
                data-testid="privacy-contact-email"
              >
                {CONTACT_EMAIL}
              </a>
              <p className="text-xs text-foreground/50 mt-2">
                We respond within 30 days as required by the NDPA 2023.
              </p>
            </div>
          </section>
        </div>

        <div className="mt-16 pt-8 border-t border-white/10 text-xs text-foreground/45 text-center">
          <p>© {new Date().getFullYear()} Cinevoya · Privacy Policy · Effective {EFFECTIVE_DATE}</p>
        </div>
      </div>
    </div>
  );
}
